import React from 'react';
import { colors, borderRadius, spacing } from '../../styles/designTokens';

/**
 * Skeleton Component
 * 
 * Loading placeholder that shows layout structure while content loads.
 * Provides better perceived performance than spinners alone.
 * 
 * Usage:
 * <Skeleton width="200px" height="24px" />
 * <Skeleton variant="circle" size="48px" />
 * <Skeleton variant="text" lines={3} />
 * 
 * Props:
 * - variant: 'rect' | 'circle' | 'text' (default: 'rect')
 * - width: String - width (default: '100%')
 * - height: String - height (default: '16px')
 * - size: String - shorthand for width and height (circles)
 * - lines: Number - number of text lines (for 'text' variant)
 * - className: Additional CSS classes
 */

const Skeleton = ({
  variant = 'rect',
  width = '100%',
  height = '16px',
  size,
  lines = 1,
  className = ''
}) => {
  const baseStyles = {
    backgroundColor: colors.neutral[200],
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
    display: 'inline-block'
  };

  const variantStyles = {
    rect: {
      borderRadius: borderRadius.md,
      width: size || width,
      height: size || height
    },
    circle: {
      borderRadius: '50%',
      width: size || width,
      height: size || height
    },
    text: {
      borderRadius: borderRadius.sm,
      height: '1em',
      width: width
    }
  };

  // For text variant with multiple lines
  if (variant === 'text' && lines > 1) {
    return (
      <div className={className} style={{ width: width }}>
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            style={{
              ...baseStyles,
              ...variantStyles.text,
              marginBottom: index < lines - 1 ? spacing.xs : 0,
              width: index === lines - 1 ? '80%' : '100%' // Last line shorter
            }}
          />
        ))}
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <div
        className={className}
        style={{
          ...baseStyles,
          ...variantStyles[variant]
        }}
        aria-busy="true"
        aria-live="polite"
        aria-label="Loading..."
      />
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </>
  );
};

/**
 * SkeletonCard Component
 * 
 * Pre-built skeleton for card layouts
 * 
 * Usage:
 * <SkeletonCard />
 */
export const SkeletonCard = ({ className = '' }) => {
  return (
    <div
      className={className}
      style={{
        padding: spacing.md,
        border: `1px solid ${colors.neutral[200]}`,
        borderRadius: borderRadius.md,
        backgroundColor: colors.neutral[50]
      }}
    >
      <Skeleton height="24px" width="60%" style={{ marginBottom: spacing.md }} />
      <Skeleton variant="text" lines={3} />
      <div style={{ marginTop: spacing.md, display: 'flex', gap: spacing.sm }}>
        <Skeleton width="80px" height="32px" />
        <Skeleton width="80px" height="32px" />
      </div>
    </div>
  );
};

/**
 * SkeletonTable Component
 * 
 * Pre-built skeleton for table layouts
 * 
 * Usage:
 * <SkeletonTable rows={5} columns={4} />
 */
export const SkeletonTable = ({ rows = 5, columns = 4, className = '' }) => {
  return (
    <div
      className={className}
      style={{
        border: `1px solid ${colors.neutral[200]}`,
        borderRadius: borderRadius.md,
        overflow: 'hidden'
      }}
    >
      {/* Table Header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: spacing.md,
          padding: spacing.md,
          backgroundColor: colors.neutral[100],
          borderBottom: `1px solid ${colors.neutral[200]}`
        }}
      >
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={index} height="20px" width="80%" />
        ))}
      </div>

      {/* Table Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: spacing.md,
            padding: spacing.md,
            borderBottom: rowIndex < rows - 1 ? `1px solid ${colors.neutral[200]}` : 'none'
          }}
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} height="16px" width="90%" />
          ))}
        </div>
      ))}
    </div>
  );
};

/**
 * SkeletonList Component
 * 
 * Pre-built skeleton for list layouts
 * 
 * Usage:
 * <SkeletonList items={5} />
 */
export const SkeletonList = ({ items = 5, showAvatar = false, className = '' }) => {
  return (
    <div className={className}>
      {Array.from({ length: items }).map((_, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
            padding: spacing.md,
            borderBottom: index < items - 1 ? `1px solid ${colors.neutral[200]}` : 'none'
          }}
        >
          {showAvatar && <Skeleton variant="circle" size="40px" />}
          <div style={{ flex: 1 }}>
            <Skeleton height="18px" width="40%" style={{ marginBottom: spacing.xs }} />
            <Skeleton height="14px" width="60%" />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * SkeletonMetricCard Component
 * 
 * Pre-built skeleton for metric/stat cards
 * 
 * Usage:
 * <SkeletonMetricCard />
 */
export const SkeletonMetricCard = ({ className = '' }) => {
  return (
    <div
      className={className}
      style={{
        padding: spacing.md,
        border: `1px solid ${colors.neutral[200]}`,
        borderRadius: borderRadius.md,
        backgroundColor: colors.neutral[50]
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
        <Skeleton variant="circle" size="48px" />
        <div style={{ flex: 1 }}>
          <Skeleton height="32px" width="60%" style={{ marginBottom: spacing.xs }} />
          <Skeleton height="14px" width="40%" />
        </div>
      </div>
    </div>
  );
};

export default Skeleton;

